import { GameState } from '@/shared/types';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';

// Define the shape of our WebSocket state
export interface WebsocketState {
  gameState: GameState | null;
  lastUpdated: {
    [key: string]: number;
  };
  connectionStatus: {
    [key: string]: 'connected' | 'disconnected' | 'error';
  };
  errors: {
    [key: string]: string | null;
  };
  [key: string]: any;
}

// Initial state
const initialState: WebsocketState = {
  gameState: null,
  lastUpdated: {},
  connectionStatus: {},
  errors: {},
};

const websocketSlice = createSlice({
  name: 'websocket',
  initialState,
  reducers: {
    // Update WebSocket data for a specific key
    updateWebsocketData: (
      state,
      action: PayloadAction<{
        key: string;
        data: any;
      }>
    ) => {

      const { key, data } = action.payload;
      // Apply transform if provided, otherwise use data as-is
      state[key] = data;
      // Update the timestamp
      state.lastUpdated[key] = Date.now();
    },

    // Update connection status
    setConnectionStatus: (
      state,
      action: PayloadAction<{
        key: string;
        status: 'connected' | 'disconnected' | 'error'
      }>
    ) => {
      const { key, status } = action.payload;
      state.connectionStatus[key] = status;
    },

    // Set error for a specific WebSocket connection
    setError: (
      state,
      action: PayloadAction<{
        key: string;
        error: string | null
      }>
    ) => {
      const { key, error } = action.payload;
      state.errors[key] = error;
    },

    // Clear data for a specific WebSocket connection
    clearWebsocketData: (
      state,
      action: PayloadAction<string>
    ) => {
      const key = action.payload;
      state[key] = null;
      state.lastUpdated[key] = 0;
      state.errors[key] = null;
    },

    // Reset all WebSocket state
    resetWebsocketState: (state) => {
      Object.keys(state).forEach(key => {
        if (key !== 'connectionStatus' && key !== 'errors' && key !== 'lastUpdated') {
          state[key] = null;
        }
      });
      state.lastUpdated = {};
      state.errors = {};
    },
  },
});

// Export actions
export const {
  updateWebsocketData,
  setConnectionStatus,
  setError,
  clearWebsocketData,
  resetWebsocketState,
} = websocketSlice.actions;

// Export reducer
export default websocketSlice.reducer;

